import React from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  Flex,
  Heading,
  Select,
  Switch,
  Text,
  cn,
  useToast,
} from '@chatsdet/ui';
import type { DigestKind } from '@chatsdet/types';
import {
  useDigestConfigs,
  useDigestPreferences,
  useRunDigest,
  useUpdateDigestConfig,
  useUpdateDigestPreference,
} from '../../../features/digests/useDigests';
import type { OrgDigestConfig } from '../../../features/digests/types';
import { cronToTime, timeToCron } from '../../../features/digests/cron';
import { browserTimezone } from '../../../features/scheduling/presets';
import { useNotificationDeliveryHealth } from '../../../features/notifications/hooks/useNotificationDeliveryHealth';
import { useSlackChannels } from '../../../features/notifications/hooks/useSlackChannels';
import { SlackDestinationsPicker } from './SlackDestinationsPicker';

const KIND_META: Array<{ kind: DigestKind; title: string; description: string }> = [
  {
    kind: 'daily',
    title: 'Daily Digest',
    description: 'A daily summary of pass rate, issues, and workflow activity.',
  },
  {
    kind: 'weekly',
    title: 'Weekly Digest',
    description: 'A weekly rollup with coverage trend and a daily breakdown.',
  },
];

function defaultConfigFor(kind: DigestKind): OrgDigestConfig {
  return {
    kind,
    enabled: false,
    cronPattern: kind === 'daily' ? '0 7 * * *' : '0 7 * * 1',
    timezone: browserTimezone(),
    destinations: [],
    updatedAt: '',
  };
}

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, hour) => ({
  id: String(hour),
  label: new Date(2000, 0, 1, hour, 0).toLocaleTimeString('en-US', {
    hour: 'numeric',
    hour12: true,
  }),
}));

const MINUTE_OPTIONS = Array.from({ length: 12 }, (_, i) => i * 5).map((minute) => ({
  id: String(minute),
  label: minute.toString().padStart(2, '0'),
}));

const TIMEZONE_OPTIONS = Intl.supportedValuesOf('timeZone').map((tz) => ({ id: tz, label: tz }));

/**
 * "Sends daily at 7:00 AM (America/Chicago)" / "Sends every Monday at 7:00 AM
 * (America/Chicago)", or null when the cron pattern doesn't parse.
 */
function formatScheduleSummary(kind: DigestKind, cronPattern: string, timezone: string): string | null {
  const time = cronToTime(cronPattern);
  if (!time) return null;
  const sendTime = new Date(2000, 0, 1, time.hour, time.minute).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
  return `Sends ${kind === 'daily' ? 'daily' : 'every Monday'} at ${sendTime} (${timezone})`;
}

/** Field-by-field equality for the parts of a digest config the editor lets a user change. */
function configsEqual(a: OrgDigestConfig, b: OrgDigestConfig): boolean {
  if (a.enabled !== b.enabled || a.cronPattern !== b.cronPattern || a.timezone !== b.timezone) {
    return false;
  }
  if (a.destinations.length !== b.destinations.length) return false;
  // Keyed by id rather than position: the picker always assigns an id, and the
  // saved config can come back from a refetch in a different order.
  const byId = new Map(b.destinations.map((destination) => [destination.id, destination]));
  return a.destinations.every((destination) => {
    const other = byId.get(destination.id);
    return (
      other !== undefined &&
      destination.slackChannelId === other.slackChannelId &&
      destination.slackChannelName === other.slackChannelName &&
      destination.deliveryMode === other.deliveryMode &&
      destination.enabled === other.enabled
    );
  });
}

function DigestKindCard({
  kind,
  title,
  description,
  saved,
  slackConnected,
  channelOptions,
}: {
  kind: DigestKind;
  title: string;
  description: string;
  saved: OrgDigestConfig | undefined;
  slackConnected: boolean;
  channelOptions: Array<{ id: string; label: string }>;
}) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const fallback = React.useMemo(() => defaultConfigFor(kind), [kind]);
  const [draft, setDraft] = React.useState<OrgDigestConfig>(saved ?? fallback);

  // Reseed the draft whenever the saved config for this kind changes (initial
  // load, or after another save elsewhere) — but not while the user is mid-edit
  // relative to their own successful save, which the mutation's onSuccess covers.
  React.useEffect(() => {
    setDraft(saved ?? fallback);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saved?.updatedAt, kind]);

  const updateConfig = useUpdateDigestConfig();
  const runDigest = useRunDigest();

  const time = cronToTime(draft.cronPattern) ?? { hour: 7, minute: 0 };
  const isDirty = !configsEqual(draft, saved ?? fallback);
  const scheduleSummary = formatScheduleSummary(kind, draft.cronPattern, draft.timezone);

  const setTime = (patch: Partial<{ hour: number; minute: number }>) => {
    const next = { ...time, ...patch };
    setDraft((current) => ({ ...current, cronPattern: timeToCron(kind, next.hour, next.minute) }));
  };

  const handleSave = async () => {
    try {
      const saved = await updateConfig.mutateAsync({
        kind: draft.kind,
        enabled: draft.enabled,
        cronPattern: draft.cronPattern,
        timezone: draft.timezone,
        destinations: draft.destinations,
      });
      setDraft(saved);
      toast.success(`${title} configuration saved`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to save digest configuration');
    }
  };

  const handleSendNow = async () => {
    try {
      await runDigest.mutateAsync({ kind });
      toast.success(`${title} queued`, {
        action: { label: 'Go to Home', onClick: () => navigate({ to: '/' }) },
      });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to queue digest');
    }
  };

  return (
    <Card variant="bordered" padding="md">
      <CardHeader>
        <Flex justify="between" align="start" gap="md">
          <div className="min-w-0">
            <Heading level={3}>{title}</Heading>
            <Text size="sm" className="text-muted-foreground">
              {description}
            </Text>
          </div>
          <Flex align="center" gap="sm" className="shrink-0 pt-0.5">
            <Badge variant={draft.enabled ? 'default' : 'secondary'}>
              {draft.enabled ? 'Enabled' : 'Off'}
            </Badge>
            <Switch
              aria-label={`${draft.enabled ? 'Disable' : 'Enable'} ${title}`}
              isSelected={draft.enabled}
              onChange={(enabled) => setDraft((current) => ({ ...current, enabled }))}
            />
          </Flex>
        </Flex>
      </CardHeader>
      <CardContent className={cn('mt-4 space-y-5 transition-opacity', !draft.enabled && 'opacity-60')}>
        <div className="max-w-md space-y-2">
          <Text weight="semibold">When it sends</Text>
          <Flex gap="sm" align="end">
            <div className="w-24">
              <Select
                label="Hour"
                items={HOUR_OPTIONS}
                selectedKey={String(time.hour)}
                onSelectionChange={(key) => setTime({ hour: Number(key) })}
              />
            </div>
            <div className="w-24">
              <Select
                label="Minute"
                items={MINUTE_OPTIONS}
                selectedKey={String(time.minute)}
                onSelectionChange={(key) => setTime({ minute: Number(key) })}
              />
            </div>
            <div className="min-w-0 flex-1">
              <Select
                label="Timezone"
                items={TIMEZONE_OPTIONS}
                selectedKey={draft.timezone}
                onSelectionChange={(key) => setDraft((current) => ({ ...current, timezone: String(key) }))}
              />
            </div>
          </Flex>
          {scheduleSummary && (
            <Text size="sm" className="text-muted-foreground">
              {scheduleSummary}
            </Text>
          )}
        </div>

        <SlackDestinationsPicker
          destinations={draft.destinations}
          onChange={(destinations) => setDraft((current) => ({ ...current, destinations }))}
          channelOptions={channelOptions}
          slackConnected={slackConnected}
        />
      </CardContent>
      <CardFooter className="justify-start gap-2 border-t border-border">
        <Button
          variant="primary"
          onPress={() => void handleSave()}
          isDisabled={updateConfig.isPending || !isDirty}
        >
          {updateConfig.isPending ? 'Saving...' : 'Save'}
        </Button>
        <Button variant="outline" onPress={() => void handleSendNow()} isDisabled={runDigest.isPending}>
          {runDigest.isPending ? 'Sending...' : 'Send now'}
        </Button>
      </CardFooter>
    </Card>
  );
}

/**
 * Read-only view of a kind's org digest config for non-admin members (spec
 * decision 16: any org member can see when and where digests post; only
 * admins can edit or send). No inputs, no Save/Send now.
 */
function DigestKindSummaryCard({
  title,
  description,
  saved,
}: {
  title: string;
  description: string;
  saved: OrgDigestConfig | undefined;
}) {
  if (!saved) {
    return (
      <Card variant="bordered" padding="md">
        <CardHeader>
          <Flex justify="between" align="start" gap="md">
            <div className="min-w-0">
              <Heading level={3}>{title}</Heading>
              <Text size="sm" className="text-muted-foreground">
                {description}
              </Text>
            </div>
            <Badge variant="secondary" className="shrink-0">
              Not set up
            </Badge>
          </Flex>
        </CardHeader>
        <CardContent className="mt-4">
          <Text size="sm" className="text-muted-foreground">
            An org admin can configure this digest.
          </Text>
        </CardContent>
      </Card>
    );
  }

  const scheduleSummary = formatScheduleSummary(saved.kind, saved.cronPattern, saved.timezone);
  const enabledDestinations = saved.destinations.filter((destination) => destination.enabled);

  return (
    <Card variant="bordered" padding="md">
      <CardHeader>
        <Flex justify="between" align="start" gap="md">
          <div className="min-w-0">
            <Heading level={3}>{title}</Heading>
            <Text size="sm" className="text-muted-foreground">
              {description}
            </Text>
          </div>
          <Badge variant={saved.enabled ? 'default' : 'secondary'} className="shrink-0">
            {saved.enabled ? 'Enabled' : 'Off'}
          </Badge>
        </Flex>
      </CardHeader>
      <CardContent className={cn('mt-4 space-y-2 transition-opacity', !saved.enabled && 'opacity-60')}>
        {saved.enabled && scheduleSummary && <Text size="sm">{scheduleSummary}</Text>}
        <Text size="sm" className="text-muted-foreground">
          {enabledDestinations.length === 0
            ? 'No Slack destinations configured.'
            : `Posts to ${enabledDestinations
                .map((destination) => `#${destination.slackChannelName ?? destination.slackChannelId}`)
                .join(', ')}`}
        </Text>
      </CardContent>
    </Card>
  );
}

function MemberPreferences() {
  const { toast } = useToast();
  const prefsQuery = useDigestPreferences();
  const updatePref = useUpdateDigestPreference();
  const prefs = prefsQuery.data;

  const setPref = (kind: DigestKind, enabled: boolean) => {
    updatePref.mutate(
      { kind, enabled },
      {
        onError: (error) =>
          toast.error(error instanceof Error ? error.message : 'Failed to save preference'),
      }
    );
  };

  return (
    <div className="space-y-3">
      <Text size="xs" weight="semibold" className="uppercase tracking-wider text-muted-foreground">
        Your Email Preferences
      </Text>
      <Text size="sm" className="text-muted-foreground">
        Get digests emailed to you when your organization has them enabled.
      </Text>
      <div className="rounded-lg border border-border bg-card divide-y divide-border overflow-hidden">
        {(['daily', 'weekly'] as const).map((kind) => (
          <div key={kind} className="flex items-center justify-between gap-4 px-4 py-3.5">
            <Text weight="medium">Email me the {kind === 'daily' ? 'Daily' : 'Weekly'} Digest</Text>
            <Switch
              isSelected={prefs?.[kind]?.enabled ?? false}
              onChange={(enabled) => setPref(kind, enabled)}
              isDisabled={!prefs || updatePref.isPending}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

export function DigestsSection({ isAdmin }: { isAdmin: boolean }) {
  const configsQuery = useDigestConfigs();
  const configsByKind = new Map((configsQuery.data ?? []).map((config) => [config.kind, config]));

  const healthQuery = useNotificationDeliveryHealth({ enabled: isAdmin });
  const slackConnected = healthQuery.data?.data.slackConnected === true;
  const slackChannelsQuery = useSlackChannels('digests', { enabled: isAdmin && slackConnected });

  return (
    <div className="space-y-8">
      <div className="space-y-2">
        <Heading level={2}>Digests</Heading>
        <Text size="sm" className="text-muted-foreground">
          Automated summaries of pass rate, issues, and workflow activity, delivered to Slack and
          email.
        </Text>
      </div>

      <MemberPreferences />

      <div className="space-y-3">
        <Text size="xs" weight="semibold" className="uppercase tracking-wider text-muted-foreground">
          Organization Schedule
        </Text>
        <Text size="sm" className="text-muted-foreground">
          {isAdmin
            ? "Configure when digests generate and where they're posted for the whole organization."
            : "When and where your organization's digests post."}
        </Text>
        <div className="space-y-4">
          {KIND_META.map((meta) =>
            isAdmin ? (
              <DigestKindCard
                key={meta.kind}
                kind={meta.kind}
                title={meta.title}
                description={meta.description}
                saved={configsByKind.get(meta.kind)}
                slackConnected={slackConnected}
                channelOptions={slackChannelsQuery.channelOptions}
              />
            ) : (
              <DigestKindSummaryCard
                key={meta.kind}
                title={meta.title}
                description={meta.description}
                saved={configsByKind.get(meta.kind)}
              />
            )
          )}
        </div>
      </div>
    </div>
  );
}
